import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger
} from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ReferralRulesLink, ReferralTermsLink } from '../components/ReferralRules';
import { TERMS_VERSION } from '../components/referralTermsText';
import {
  Gift, Copy, Check, Wallet, Clock, TrendingUp, Users, Loader2,
  Share2, Mail, MessageCircle, ArrowDownToLine, BookOpen, ChevronDown, ChevronUp, MessageSquareText,
  FileText, Sparkles
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// WO-71 — country of tax residence on the withdrawal form (ISO 3166 codes).
const COUNTRY_CODES = ('AD AE AF AG AI AL AM AO AR AT AU AW AZ BA BB BD BE BF BG BH BI BJ BM BN BO BR BS BT BW BY BZ '
  + 'CA CD CF CG CH CI CL CM CN CO CR CU CV CY CZ DE DJ DK DM DO DZ EC EE EG ER ES ET FI FJ FR GA GB GD GE GH GI GM GN '
  + 'GQ GR GT GW GY HK HN HR HT HU ID IE IL IN IQ IR IS IT JM JO JP KE KG KH KM KN KR KW KZ LA LB LC LI LK LR LS LT LU '
  + 'LV LY MA MC MD ME MG MK ML MM MN MO MR MT MU MV MW MX MY MZ NA NE NG NI NL NO NP NZ OM PA PE PG PH PK PL PS PT PY '
  + 'QA RO RS RU RW SA SC SD SE SG SI SK SL SM SN SO SR SS SV SY SZ TD TG TH TJ TM TN TR TT TW TZ UA UG US UY UZ VE VN '
  + 'YE ZA ZM ZW').split(' ');

const STATUS_COLORS = {
  active: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400',
  pending: 'bg-amber-500/15 text-amber-600 dark:text-amber-400',
  approved: 'bg-blue-500/15 text-blue-600 dark:text-blue-400',
  withdrawn: 'bg-purple-500/15 text-purple-600 dark:text-purple-400',
  paid: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400',
  rejected: 'bg-red-500/15 text-red-600 dark:text-red-400',
  reversed: 'bg-red-500/15 text-red-600 dark:text-red-400',
  expired: 'bg-gray-500/15 text-gray-500',
  fraud_review: 'bg-orange-500/15 text-orange-600 dark:text-orange-400',
  under_review: 'bg-orange-500/15 text-orange-600 dark:text-orange-400',
  on_hold_spain: 'bg-amber-500/15 text-amber-600 dark:text-amber-400',
};

const StatusBadge = ({ status }) => (
  <Badge className={`${STATUS_COLORS[status] || 'bg-muted text-muted-foreground'} border-0 capitalize`}>
    {(status || '').replace(/_/g, ' ')}
  </Badge>
);

const MetricCard = ({ icon: Icon, label, value, color, testid }) => (
  <Card data-testid={testid}>
    <CardContent className="p-5 flex items-center gap-4">
      <div className={`p-3 rounded-xl ${color}`}>
        <Icon className="w-6 h-6 text-white" />
      </div>
      <div>
        <p className="text-sm text-muted-foreground">{label}</p>
        <p className="text-2xl font-bold" style={{ fontFamily: "'Sora', sans-serif" }}>{value}</p>
      </div>
    </CardContent>
  </Card>
);

export default function ReferralDashboardPage() {
  const { token } = useAuth();
  const { language } = useLanguage();
  const isAr = language === 'ar';

  const [loading, setLoading] = useState(true);
  const [code, setCode] = useState('');
  const [link, setLink] = useState('');
  const [copied, setCopied] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  const [commissions, setCommissions] = useState([]);
  const [terms, setTerms] = useState(null);
  // WO-71 — Referral Programme Terms v1.0: the code appears once they are accepted
  const [termsStatus, setTermsStatus] = useState(null);
  const [codeState, setCodeState] = useState('ok');

  const authHeaders = useCallback(() => ({
    'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json'
  }), [token]);

  const load = useCallback(async () => {
    try {
      const [codeRes, dashRes, commRes, termsRes, tsRes] = await Promise.all([
        fetch(`${API_URL}/api/referral/my-code`, { headers: authHeaders() }),
        fetch(`${API_URL}/api/referral/dashboard`, { headers: authHeaders() }),
        fetch(`${API_URL}/api/referral/commissions?page_size=50`, { headers: authHeaders() }),
        fetch(`${API_URL}/api/referral/terms`, { headers: authHeaders() }),
        fetch(`${API_URL}/api/referral/terms-status`, { headers: authHeaders() }),
      ]);
      const codeData = await codeRes.json();
      setCodeState(codeRes.ok ? 'ok' : (codeData.detail || 'terms_required'));
      setTermsStatus(tsRes.ok ? await tsRes.json() : null);
      const dashData = await dashRes.json();
      const commData = await commRes.json();
      const termsData = await termsRes.json();
      setCode(codeData.code || '');
      setLink(codeData.link || '');
      setDashboard(dashData);
      setCommissions(commData.items || []);
      setTerms(termsData);
    } catch (e) {
      toast.error('Failed to load referral data');
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => { load(); }, [load]);

  const copyLink = () => {
    navigator.clipboard.writeText(link);
    setCopied(true);
    toast.success(isAr ? 'تم نسخ الرابط' : 'Link copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const commissionPct = terms?.commission_percent ?? 15;
  const discountPct = terms?.referred_discount_percent ?? 15;
  const durationMonths = terms?.referral_duration_months ?? 12;

  const shareText = isAr
    ? `انضم إلى Mid2Bid واحصل على خصم ${discountPct}٪ على معلومات ومناقصات الخليج!`
    : `Join Mid2Bid and get ${discountPct}% off GCC construction intelligence & tenders!`;
  // WO-71 (BILAL 20/10/2026) — codes travel only privately, one person at a time.
  // The LinkedIn "share" button published a PUBLIC post with the code: removed. For a
  // LinkedIn private message, the person copies the message and pastes it there.
  const shareLinks = {
    whatsapp: `https://wa.me/?text=${encodeURIComponent(shareText + ' ' + link)}`,
    email: `mailto:?subject=${encodeURIComponent('Mid2Bid invitation')}&body=${encodeURIComponent(shareText + '\n\n' + link)}`,
  };
  const copyMessage = () => {
    navigator.clipboard.writeText(`${shareText}\n\n${link}`);
    toast.success(isAr ? 'تم نسخ الرسالة — الصقها في رسالة خاصة' : 'Message copied — paste it into a private message');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32" data-testid="referral-loading">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const m = dashboard?.metrics || {};

  return (
    <div className="space-y-6" data-testid="referral-dashboard-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-2.5 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500">
          <Gift className="w-6 h-6 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: "'Sora', sans-serif" }}>
            {isAr ? 'أحِل واربح' : 'Refer & Earn'}
          </h1>
          <p className="text-sm text-muted-foreground" data-testid="referral-headline">
            {isAr
              ? `اربح ${commissionPct}٪ عمولة لمدة ${durationMonths} شهراً عن كل إحالة`
              : `Earn ${commissionPct}% commission for ${durationMonths} months on every referral`}
          </p>
        </div>
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard icon={Wallet} label={isAr ? 'الرصيد المتاح' : 'Available Balance'}
          value={m.available_balance_display} color="bg-gradient-to-br from-emerald-500 to-teal-500"
          testid="metric-available" />
        <MetricCard icon={Clock} label={isAr ? 'قيد الانتظار' : 'Pending'}
          value={m.pending_display} color="bg-gradient-to-br from-amber-500 to-orange-500"
          testid="metric-pending" />
        <MetricCard icon={TrendingUp} label={isAr ? 'إجمالي الأرباح' : 'Lifetime Earnings'}
          value={m.lifetime_display} color="bg-gradient-to-br from-blue-500 to-indigo-500"
          testid="metric-lifetime" />
        <MetricCard icon={Users} label={isAr ? 'إجمالي الإحالات' : 'Total Referrals'}
          value={m.total_referrals} color="bg-gradient-to-br from-purple-500 to-pink-500"
          testid="metric-total" />
      </div>

      {/* WO-71 — one quick tick before the code appears (Terms v1.0) */}
      {codeState === 'terms_required' && (
        <JoinCard isAr={isAr} token={token} onDone={load} discountPct={discountPct} commissionPct={commissionPct} />
      )}
      {codeState.startsWith('participation_') && (
        <ParticipationCard isAr={isAr} token={token} onDone={load} status={codeState.replace('participation_', '')} />
      )}

      {/* Share card */}
      {codeState === 'ok' && (
      <Card className="border-2 border-emerald-500/20">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Share2 className="w-5 h-5 text-emerald-500" />
            {isAr ? 'شارك رابط الإحالة الخاص بك' : 'Share your referral link'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="flex-1 flex items-center gap-2 rounded-lg border bg-muted/40 px-3 py-2">
              <code className="text-sm flex-1 truncate" data-testid="referral-link">{link}</code>
              <Badge variant="outline" className="font-mono" data-testid="referral-code">{code}</Badge>
            </div>
            <Button onClick={copyLink} data-testid="copy-link-btn" className="shrink-0">
              {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
              {copied ? (isAr ? 'تم النسخ' : 'Copied') : (isAr ? 'نسخ' : 'Copy')}
            </Button>
          </div>
          <div className="flex flex-wrap gap-2">
            <a href={shareLinks.whatsapp} target="_blank" rel="noreferrer" data-testid="share-whatsapp">
              <Button variant="outline" size="sm"><MessageCircle className="w-4 h-4 mr-2 text-green-500" />WhatsApp</Button>
            </a>
            <Button variant="outline" size="sm" onClick={copyMessage} data-testid="share-copy-message">
              <MessageSquareText className="w-4 h-4 mr-2 text-blue-600" />
              {isAr ? 'نسخ رسالة خاصة (لينكدإن وغيره)' : 'Copy a private message (LinkedIn, etc.)'}
            </Button>
            <a href={shareLinks.email} data-testid="share-email">
              <Button variant="outline" size="sm"><Mail className="w-4 h-4 mr-2 text-muted-foreground" />Email</Button>
            </a>
          </div>
          <div className="rounded-md bg-muted/50 p-3 text-xs text-muted-foreground space-y-1" data-testid="share-private-rule">
            <p>
              {isAr
                ? 'شارك رابطك بشكل شخصي — مع كل شخص على حدة، برسالة أو بريد إلكتروني أو في حديث. نرجو ألا تنشره علناً: العروض العامة تأتي من صفحات Mid2Bid فقط. ويمكنك دائماً الحديث عن Mid2Bid ومشاركة منشوراتنا.'
                : 'Share your link personally — one person at a time, by message, e-mail or in conversation. Please don’t post it publicly: public offers come only from Mid2Bid’s own pages. You are always welcome to talk about Mid2Bid and share our posts.'}
            </p>
            <div className="flex flex-wrap gap-x-4 gap-y-1">
              <ReferralRulesLink isAr={isAr} />
              <ReferralTermsLink isAr={isAr} />
              <button type="button" className="underline underline-offset-2" data-testid="stop-taking-part"
                onClick={async () => {
                  await fetch(`${API_URL}/api/referral/participation`, { method: 'POST', headers: authHeaders(),
                    body: JSON.stringify({ status: 'ended' }) });
                  load();
                }}>
                {isAr ? 'التوقف عن المشاركة' : 'Stop taking part'}
              </button>
            </div>
          </div>
        </CardContent>
      </Card>
      )}

      {/* WO-71 — the whole procedure in plain words, numbers read from the live settings */}
      <HowItWorks terms={terms} isAr={isAr} />

      {/* Tabs */}
      <Tabs defaultValue="referrals" className="w-full">
        <TabsList>
          <TabsTrigger value="referrals" data-testid="tab-referrals">{isAr ? 'إحالاتي' : 'My Referrals'}</TabsTrigger>
          <TabsTrigger value="commissions" data-testid="tab-commissions">{isAr ? 'العمولات' : 'Commissions'}</TabsTrigger>
          <TabsTrigger value="withdrawals" data-testid="tab-withdrawals">{isAr ? 'السحوبات' : 'Withdrawals'}</TabsTrigger>
        </TabsList>

        <TabsContent value="referrals">
          <Card><CardContent className="p-0">
            <Table>
              <TableHeader><TableRow>
                <TableHead>{isAr ? 'العميل' : 'Customer'}</TableHead>
                <TableHead>{isAr ? 'الحالة' : 'Status'}</TableHead>
                <TableHead>{isAr ? 'الإيرادات' : 'Revenue'}</TableHead>
                <TableHead>{isAr ? 'عمولتك' : 'Your Commission'}</TableHead>
                <TableHead>{isAr ? 'ينتهي في' : 'Expires'}</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {(dashboard?.referrals || []).length === 0 ? (
                  <TableRow><TableCell colSpan={5} className="text-center py-10 text-muted-foreground">
                    {isAr ? 'لا توجد إحالات بعد. شارك رابطك للبدء!' : 'No referrals yet. Share your link to get started!'}
                  </TableCell></TableRow>
                ) : dashboard.referrals.map((r) => (
                  <TableRow key={r.id} data-testid={`referral-row-${r.id}`}>
                    <TableCell className="font-medium">{r.domain}</TableCell>
                    <TableCell><StatusBadge status={r.status} /></TableCell>
                    <TableCell>{r.revenue_display}</TableCell>
                    <TableCell className="font-semibold text-emerald-600">{r.commission_display}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {r.expires_at ? new Date(r.expires_at).toLocaleDateString() : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="commissions">
          <Card><CardContent className="p-0">
            <Table>
              <TableHeader><TableRow>
                <TableHead>{isAr ? 'التاريخ' : 'Date'}</TableHead>
                <TableHead>{isAr ? 'العميل' : 'Customer'}</TableHead>
                <TableHead>{isAr ? 'النوع' : 'Type'}</TableHead>
                <TableHead>{isAr ? 'الدفعة' : 'Payment'}</TableHead>
                <TableHead>{isAr ? 'العمولة' : 'Commission'}</TableHead>
                <TableHead>{isAr ? 'الحالة' : 'Status'}</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {commissions.length === 0 ? (
                  <TableRow><TableCell colSpan={6} className="text-center py-10 text-muted-foreground">
                    {isAr ? 'لا توجد عمولات بعد' : 'No commissions yet'}
                  </TableCell></TableRow>
                ) : commissions.map((c) => (
                  <TableRow key={c.id} data-testid={`commission-row-${c.id}`}>
                    <TableCell className="text-sm">{c.date ? new Date(c.date).toLocaleDateString() : '—'}</TableCell>
                    <TableCell>{c.domain}</TableCell>
                    <TableCell className="capitalize text-sm">{(c.revenue_type || '').replace('_', ' ')}{c.report_name ? ` · ${c.report_name}` : ''}</TableCell>
                    <TableCell>{c.payment_display}</TableCell>
                    <TableCell className="font-semibold text-emerald-600">{c.commission_display}</TableCell>
                    <TableCell><StatusBadge status={c.status} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="withdrawals">
          <div className="flex justify-end mb-4">
            <WithdrawalDialog token={token} isAr={isAr} minCents={terms?.min_withdrawal_cents ?? 5000}
              targetDays={terms?.payout_target_days ?? 14} ended={termsStatus?.participation === 'ended'}
              available={dashboard?.earnings?.available_balance_cents || 0} onDone={load} />
          </div>
          <Card><CardContent className="p-0">
            <Table>
              <TableHeader><TableRow>
                <TableHead>{isAr ? 'التاريخ' : 'Date'}</TableHead>
                <TableHead>{isAr ? 'المبلغ' : 'Amount'}</TableHead>
                <TableHead>{isAr ? 'الطريقة' : 'Method'}</TableHead>
                <TableHead>{isAr ? 'الحالة' : 'Status'}</TableHead>
                <TableHead>{isAr ? 'تاريخ الدفع' : 'Paid on'}</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {(dashboard?.withdrawals || []).length === 0 ? (
                  <TableRow><TableCell colSpan={5} className="text-center py-10 text-muted-foreground">
                    {isAr ? 'لا توجد سحوبات بعد' : 'No withdrawals yet'}
                  </TableCell></TableRow>
                ) : dashboard.withdrawals.map((w) => (
                  <TableRow key={w._id} data-testid={`withdrawal-row-${w._id}`}>
                    <TableCell className="text-sm">{w.requested_at ? new Date(w.requested_at).toLocaleDateString() : '—'}</TableCell>
                    <TableCell className="font-semibold">{w.amount_display}
                      {w.withheld_cents > 0 && (
                        <div className="text-xs font-normal text-muted-foreground">
                          {isAr ? `بعد الضريبة: ${(w.net_cents / 100).toFixed(2)} دولار` : `after tax: $${(w.net_cents / 100).toFixed(2)}`}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-sm capitalize">{(w.method || '').replace('_', ' ')}</TableCell>
                    <TableCell><StatusBadge status={w.status} /></TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {w.status === 'paid' ? (w.paid_on || (w.processed_at ? new Date(w.processed_at).toLocaleDateString() : '—')) : '—'}
                      {w.status === 'paid' && w.document_number && (
                        <button type="button" className="ms-2 inline-flex items-center gap-1 underline text-xs" data-testid={`statement-${w._id}`}
                          onClick={() => openStatement(token, w._id)}>
                          <FileText className="w-3 h-3" />{w.document_number}
                        </button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent></Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

// WO-71 — open the payment document (statement / self-billed invoice) with the sign-in header
async function openStatement(token, id) {
  try {
    const r = await fetch(`${API_URL}/api/referral/withdrawals/${id}/statement`, { headers: { Authorization: `Bearer ${token}` } });
    if (!r.ok) throw new Error(String(r.status));
    const url = URL.createObjectURL(new Blob([await r.text()], { type: 'text/html' }));
    window.open(url, '_blank', 'noopener');
  } catch (e) { toast.error('The document could not be opened. Please try again.'); }
}

// WO-71 — "Join in" card: one tick (Referral Programme Terms v1.0, BILAL 23/10/2026).
function JoinCard({ isAr, token, onDone, discountPct, commissionPct }) {
  const [ticked, setTicked] = useState(false);
  const [busy, setBusy] = useState(false);
  const join = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${API_URL}/api/referral/accept-terms`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ version: TERMS_VERSION }),
      });
      if (!r.ok) throw new Error((await r.json()).detail || 'error');
      toast.success(isAr ? 'أهلاً بك! رابطك جاهز.' : 'Welcome aboard — your link is ready!');
      onDone();
    } catch (e) { toast.error(isAr ? 'تعذّر الحفظ، حاول مرة أخرى.' : 'Could not save. Please try again.'); }
    finally { setBusy(false); }
  };
  return (
    <Card className="border-2 border-emerald-500/30" data-testid="referral-join-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Sparkles className="w-5 h-5 text-emerald-500" />
          {isAr ? 'جاهز للبدء؟ خطوة واحدة صغيرة' : 'Ready to start? Just one quick step'}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <p className="text-muted-foreground">
          {isAr
            ? `أصدقاؤك يحصلون على خصم ${discountPct}٪، وأنت تحصل على ${commissionPct}٪ من مدفوعاتهم. كل ما نطلبه: شارك رابطك بشكل شخصي، وليس في منشورات عامة.`
            : `Your contacts get ${discountPct}% off, and you earn ${commissionPct}% of what they pay. All we ask: share your link personally, not in public posts.`}
        </p>
        <label className="flex items-start gap-2 cursor-pointer">
          <input type="checkbox" className="mt-1" checked={ticked} onChange={(e) => setTicked(e.target.checked)} data-testid="referral-terms-tick" />
          <span>I have read and accept the <ReferralTermsLink isAr={false} label={`Referral Programme Terms (version ${TERMS_VERSION})`} />.</span>
        </label>
        <Button onClick={join} disabled={!ticked || busy} data-testid="referral-join-btn">
          {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          {isAr ? 'احصل على رابطي' : 'Get my link'}
        </Button>
      </CardContent>
    </Card>
  );
}

// WO-71 — Premium only (Ambassador clause A5) or stopped (clause 12.1): one click to take part again.
function ParticipationCard({ isAr, token, onDone, status }) {
  const rejoin = async () => {
    await fetch(`${API_URL}/api/referral/participation`, {
      method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ status: 'active' }),
    });
    onDone();
  };
  return (
    <Card data-testid="referral-participation-card">
      <CardContent className="p-5 space-y-2 text-sm">
        <p>
          {status === 'premium_only'
            ? (isAr ? 'اخترت Premium فقط، دون جانب العمولات. يمكنك الانضمام في أي وقت.' : 'You chose Premium only, without the commission side. You can join in any time.')
            : (isAr ? 'لقد توقفت عن المشاركة. ما زال بإمكانك طلب دفع أي رصيد متاح، مهما كان مبلغه.' : 'You have stopped taking part. You can still ask to be paid any available balance, whatever the amount.')}
        </p>
        <Button size="sm" variant="outline" onClick={rejoin} data-testid="referral-rejoin-btn">
          {isAr ? 'المشاركة مجدداً' : 'Take part again'}
        </Button>
      </CardContent>
    </Card>
  );
}

// WO-71 — payment request form. Friendly framing; the fields and the three confirmations
// are the ones the Referral Programme Terms ask for (clauses 7 and 8, BILAL 23/10/2026).
function WithdrawalDialog({ token, isAr, available, onDone, minCents = 5000, targetDays = 14, ended = false }) {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [prefilled, setPrefilled] = useState(false);
  const empty = {
    amount: (available / 100).toFixed(2), method: 'gcc_bank',
    bank_name: '', account_holder: '', account_number: '', swift_bic: '',
    acting_as: 'person', legal_name: '', company_registration: '', company_vat: '',
    address_line1: '', city: '', postal_code: '', address_country: '', tax_id: '',
    tax_residence: '', tax_confirmed: false, decl_true: false, decl_own_account: false, decl_allowed: false,
    save_details: true,
  };
  const [form, setForm] = useState(empty);
  const [wh, setWh] = useState(null);           // deduction preview (clause 8.2)
  const [cert, setCert] = useState(null);        // certificate on file
  const [certFile, setCertFile] = useState(null);
  const [certDate, setCertDate] = useState('');
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const minDisplay = (minCents / 100).toFixed(2);
  const countries = React.useMemo(() => {
    let dn = null;
    try { dn = new Intl.DisplayNames([isAr ? 'ar' : 'en'], { type: 'region' }); } catch (e) { dn = null; }
    return COUNTRY_CODES.map((c) => [c, (dn ? dn.of(c) : c) || c])
      .sort((a, b) => a[1].localeCompare(b[1], isAr ? 'ar' : 'en'));
  }, [isAr]);
  const H = { Authorization: `Bearer ${token}` };

  const preview = async (country) => {
    if (!country) { setWh(null); return; }
    try {
      const r = await fetch(`${API_URL}/api/referral/withholding-preview?country=${country}`, { headers: H });
      setWh(r.ok ? await r.json() : null);
    } catch (e) { setWh(null); }
  };

  const openDialog = async (v) => {
    setOpen(v);
    if (!v) return;
    setForm({ ...empty, amount: (available / 100).toFixed(2) });
    setPrefilled(false);
    try {
      const r = await fetch(`${API_URL}/api/referral/payout-details`, { headers: H });
      const d = r.ok ? await r.json() : null;
      if (d) {
        setCert(d.certificate || null);
        if (d.withholding) setWh(d.withholding);
        if (d.found) {
          setForm((p) => {
            const n = { ...p };
            Object.keys(empty).forEach((k) => { if (typeof d[k] === 'string' && d[k]) n[k] = d[k]; });
            return n;
          });
          setPrefilled(true);
        } else if (d.tax_residence) {
          setForm((p) => ({ ...p, tax_residence: d.tax_residence }));
        }
      }
    } catch (e) { /* the form simply stays empty */ }
  };

  const uploadCert = async () => {
    if (!certFile || !certDate) { toast.error(isAr ? 'اختر الملف وتاريخ إصداره' : 'Choose the file and its issue date'); return; }
    const fd = new FormData();
    fd.append('file', certFile); fd.append('issue_date', certDate); fd.append('country', form.tax_residence);
    try {
      const r = await fetch(`${API_URL}/api/referral/certificate`, { method: 'POST', headers: H, body: fd });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'error');
      toast.success(d.message);
      setCert({ status: 'pending', country: form.tax_residence });
      setWh(d.withholding);
    } catch (e) { toast.error(e.message); }
  };

  const submit = async () => {
    const cents = Math.round(parseFloat(form.amount || '0') * 100);
    if (!cents || (!ended && cents < minCents)) { toast.error(isAr ? `الحد الأدنى ${minDisplay} دولار` : `Minimum withdrawal is $${minDisplay}`); return; }
    if (cents > available) { toast.error(isAr ? 'المبلغ يتجاوز الرصيد المتاح' : 'Amount exceeds available balance'); return; }
    setSubmitting(true);
    try {
      const { amount, tax_confirmed, ...rest } = form;
      const res = await fetch(`${API_URL}/api/referral/withdrawal`, {
        method: 'POST', headers: { ...H, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...rest, amount_cents: cents, tax_residence_confirmed: tax_confirmed }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Request failed');
      toast.success(data.on_hold
        ? (isAr ? 'تم استلام طلبك. سنتواصل معك لترتيب الضريبة الإسبانية قبل الدفع.' : 'Request received. We will contact you to set up Spanish tax before paying.')
        : (isAr ? 'تم استلام طلبك — شكراً!' : 'Request received — thank you!'));
      setOpen(false);
      onDone();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const gross = Math.round(parseFloat(form.amount || '0') * 100) || 0;
  const pct = wh && wh.pct ? wh.pct : 0;
  const tax = Math.round(gross * pct / 100);
  const tick = (k, text, testid) => (
    <label className="flex items-start gap-2 text-xs cursor-pointer">
      <input type="checkbox" className="mt-0.5" checked={form[k]} onChange={(e) => set(k, e.target.checked)} data-testid={testid} />
      <span>{text}</span>
    </label>
  );
  const field = (k, label, testid, extra = {}) => (
    <div className="space-y-1"><Label className="text-xs">{label}</Label>
      <Input value={form[k]} onChange={(e) => set(k, e.target.value)} data-testid={testid} {...extra} /></div>
  );

  return (
    <Dialog open={open} onOpenChange={openDialog}>
      <DialogTrigger asChild>
        <Button disabled={ended ? available <= 0 : available < minCents} data-testid="request-withdrawal-btn">
          <ArrowDownToLine className="w-4 h-4 mr-2" />
          {isAr ? 'طلب سحب' : 'Request Withdrawal'}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-xl max-h-[calc(100dvh-2rem)] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isAr ? 'طلب سحب' : 'Request Withdrawal'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            {isAr ? 'الرصيد المتاح:' : 'Available balance:'} <span className="font-semibold text-foreground">${(available / 100).toFixed(2)}</span>
            {!ended && <>{' · '}{isAr ? `الحد الأدنى ${minDisplay} دولار` : `min $${minDisplay}`}</>}
          </p>
          <p className="text-xs rounded-md bg-muted/50 px-3 py-2" data-testid="withdrawal-intro">
            {prefilled
              ? (isAr ? 'عبّأنا بياناتك من المرة السابقة. عدّلها فقط إذا تغيّر شيء.' : 'We filled in your details from last time. Change them only if something is different.')
              : (isAr ? 'في المرة الأولى نحتاج بعض البيانات لحساباتنا. في المرات التالية نعبّئها لك.' : 'The first time, we need a few details for our accounting. Next time we fill them in for you.')}
          </p>
          <div className="space-y-2">
            <Label>{isAr ? 'المبلغ (دولار)' : 'Amount (USD)'}</Label>
            <Input type="number" step="0.01" value={form.amount} onChange={(e) => set('amount', e.target.value)} data-testid="withdrawal-amount" />
          </div>

          <div className="space-y-2">
            <p className="text-sm font-medium">{isAr ? 'من يستلم الدفعة' : 'Who receives the payment'}</p>
            <div className="flex gap-4 text-sm">
              {[['person', isAr ? 'شخص' : 'A private person'], ['company', isAr ? 'شركة' : 'A company']].map(([v, l]) => (
                <label key={v} className="flex items-center gap-1 cursor-pointer">
                  <input type="radio" name="m2b-acting" checked={form.acting_as === v} onChange={() => set('acting_as', v)} data-testid={`withdrawal-acting-${v}`} />{l}
                </label>
              ))}
            </div>
            {field('legal_name', form.acting_as === 'company'
              ? (isAr ? 'الاسم القانوني للشركة' : 'Company legal name')
              : (isAr ? 'اسمك القانوني الكامل' : 'Your full legal name'), 'withdrawal-legal-name')}
            {form.acting_as === 'company' && (
              <div className="grid grid-cols-2 gap-3">
                {field('company_registration', isAr ? 'رقم السجل التجاري' : 'Registration number', 'withdrawal-reg')}
                {field('company_vat', isAr ? 'الرقم الضريبي (VAT)' : 'VAT number', 'withdrawal-vat')}
              </div>
            )}
            {field('tax_id', isAr ? 'الرقم الضريبي (أو رقم الهوية / الإقامة إذا لم يوجد)' : 'Tax number (or national ID / iqama / Emirates ID if your country has none for individuals)', 'withdrawal-tax-id')}
          </div>

          <div className="space-y-2">
            <p className="text-sm font-medium">{isAr ? 'العنوان البريدي' : 'Postal address'}</p>
            {field('address_line1', isAr ? 'العنوان' : 'Address', 'withdrawal-address')}
            <div className="grid grid-cols-3 gap-3">
              {field('city', isAr ? 'المدينة' : 'City', 'withdrawal-city')}
              {field('postal_code', isAr ? 'الرمز البريدي' : 'Postal code', 'withdrawal-postal')}
              <div className="space-y-1"><Label className="text-xs">{isAr ? 'البلد' : 'Country'}</Label>
                <select value={form.address_country} onChange={(e) => set('address_country', e.target.value)} data-testid="withdrawal-address-country"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-2 py-2 text-sm">
                  <option value="">—</option>
                  {countries.map(([c, n]) => <option key={c} value={c}>{n}</option>)}
                </select></div>
            </div>
          </div>

          <div className="space-y-2">
            <Label>{isAr ? 'بلد الإقامة الضريبية' : 'Country of tax residence'}</Label>
            <select value={form.tax_residence} onChange={(e) => { set('tax_residence', e.target.value); preview(e.target.value); }}
              data-testid="withdrawal-tax-country" className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
              <option value="">{isAr ? 'اختر البلد' : 'Select your country'}</option>
              {countries.map(([c, n]) => <option key={c} value={c}>{n}</option>)}
            </select>
            {tick('tax_confirmed', isAr ? 'هذا بلد إقامتي الضريبية، وسأبلغ Mid2Bid خلال 30 يوماً إذا تغيّر.' : 'This is my country of tax residence, and I will tell Mid2Bid within 30 days if it changes.', 'withdrawal-tax-confirm')}
          </div>

          {wh && wh.hold && (
            <p className="text-xs rounded-md bg-muted/50 px-3 py-2" data-testid="withdrawal-spain">
              {isAr ? 'بما أنك مقيم في إسبانيا وتُحيل عملاء بانتظام، سنتواصل معك قبل الدفعة الأولى لترتيب الضريبة الإسبانية.' : 'As you live in Spain and refer customers regularly, we will contact you before your first payment to set up Spanish tax.'}
            </p>
          )}
          {wh && pct > 0 && (
            <div className="rounded-md bg-muted/50 p-3 text-xs space-y-2" data-testid="withdrawal-withholding">
              <p>
                {isAr
                  ? `لأنك تُحيل عملاء بانتظام، يطلب منا القانون الإسباني خصم ${pct}٪: من ${(gross / 100).toFixed(2)} دولار نخصم ${(tax / 100).toFixed(2)} دولار، وتتسلّم ${((gross - tax) / 100).toFixed(2)} دولار.`
                  : `Because you refer customers regularly, Spanish law asks us to deduct ${pct}%: from $${(gross / 100).toFixed(2)} we deduct $${(tax / 100).toFixed(2)}, and you receive $${((gross - tax) / 100).toFixed(2)}.`}
              </p>
              {cert && cert.status === 'pending' ? (
                <p>{isAr ? 'استلمنا شهادة إقامتك الضريبية وسنراجعها قبل الدفع؛ إذا قُبلت لن نخصم شيئاً.' : 'We have your tax-residence certificate and will check it before paying; if it is accepted, nothing is deducted.'}</p>
              ) : (
                <div className="space-y-1">
                  <p>{isAr ? 'يمكنك تجنّب الخصم بشهادة إقامة ضريبية من بلدك (عمرها أقل من سنة):' : 'You can avoid it with a tax-residence certificate from your country (less than a year old):'}</p>
                  <div className="flex flex-wrap items-center gap-2">
                    <input type="file" accept="application/pdf,image/jpeg,image/png" onChange={(e) => setCertFile(e.target.files?.[0] || null)} data-testid="withdrawal-cert-file" />
                    <Input type="date" value={certDate} onChange={(e) => setCertDate(e.target.value)} className="h-8 w-[150px]" data-testid="withdrawal-cert-date" />
                    <Button type="button" size="sm" variant="outline" onClick={uploadCert} data-testid="withdrawal-cert-upload">{isAr ? 'رفع' : 'Upload'}</Button>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="space-y-2">
            <p className="text-sm font-medium">{isAr ? 'الحساب البنكي (باسمك)' : 'Bank account (in your own name)'}</p>
            <Select value={form.method} onValueChange={(v) => set('method', v)}>
              <SelectTrigger data-testid="withdrawal-method"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="gcc_bank">{isAr ? 'تحويل بنكي خليجي' : 'GCC Bank Transfer'}</SelectItem>
                <SelectItem value="international_bank">{isAr ? 'تحويل بنكي دولي' : 'International Bank Transfer'}</SelectItem>
              </SelectContent>
            </Select>
            <div className="grid grid-cols-2 gap-3">
              {field('bank_name', isAr ? 'اسم البنك' : 'Bank name', 'withdrawal-bank')}
              {field('account_holder', isAr ? 'اسم صاحب الحساب' : 'Account holder', 'withdrawal-holder')}
              {field('account_number', isAr ? 'رقم الحساب / IBAN' : 'Account no. / IBAN', 'withdrawal-account')}
              {field('swift_bic', 'SWIFT / BIC', 'withdrawal-swift')}
            </div>
          </div>

          <div className="space-y-1.5" data-testid="withdrawal-declarations">
            {tick('decl_true', 'The information I have given is true.', 'withdrawal-decl-true')}
            {tick('decl_own_account', 'The bank account is in my own name.', 'withdrawal-decl-own')}
            {tick('decl_allowed', 'The law where I live and my employment terms allow me to receive this commission.', 'withdrawal-decl-allowed')}
            {tick('save_details', isAr ? 'احفظ بياناتي للمرة القادمة' : 'Save my details for next time', 'withdrawal-save')}
          </div>
        </div>
        <div className="text-xs text-muted-foreground space-y-1">
          <p data-testid="withdrawal-pay-line">
            We normally pay by bank transfer within {targetDays} days of your request, and at the latest within 30 days once we have everything we need. We e-mail you when it is paid. By requesting payment you accept the <ReferralTermsLink isAr={false} label="Referral Programme Terms" />.
          </p>
          {prefilled && (
            <button type="button" className="underline" data-testid="withdrawal-delete-saved"
              onClick={async () => { await fetch(`${API_URL}/api/referral/payout-details`, { method: 'DELETE', headers: H }); setPrefilled(false); setForm({ ...empty }); toast.success(isAr ? 'حُذفت بياناتك المحفوظة' : 'Your saved details were deleted'); }}>
              {isAr ? 'احذف بياناتي المحفوظة' : 'Delete my saved details'}
            </button>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>{isAr ? 'إلغاء' : 'Cancel'}</Button>
          <Button onClick={submit} disabled={submitting} data-testid="withdrawal-submit">
            {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {isAr ? 'إرسال الطلب' : 'Submit request'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// WO-71 — "How Refer & Earn works": BILAL's approved text (23/10/2026, Part 1 §5.1),
// with the numbers read from the live settings (/api/referral/terms).
function HowItWorks({ terms, isAr }) {
  const [open, setOpen] = useState(() => {
    try { return localStorage.getItem('m2b_referral_howto_closed') !== '1'; } catch (e) { return true; }
  });
  const toggle = () => {
    const next = !open;
    setOpen(next);
    try { localStorage.setItem('m2b_referral_howto_closed', next ? '0' : '1'); } catch (e) { /* ignore */ }
  };
  const t = {
    disc: terms?.referred_discount_percent ?? 15,
    discM: terms?.referred_discount_months ?? 12,
    comm: terms?.commission_percent ?? 15,
    commM: terms?.referral_duration_months ?? 12,
    entM: terms?.enterprise_commission_months ?? 24,
    hold: terms?.pending_days ?? 30,
    min: ((terms?.min_withdrawal_cents ?? 5000) / 100).toFixed(2),
    pay: terms?.payout_target_days ?? 14,
  };
  const steps = isAr ? [
    ['شارك رابطك أو رمزك — بشكل شخصي', `أرسله لمن تعرفهم، كل شخص على حدة، برسالة أو بريد إلكتروني أو في حديث. نرجو ألا تنشره علناً: العروض العامة تأتي من صفحات Mid2Bid فقط. كل من يسجّل عبره يحصل على خصم ${t.disc}٪ على كل ما يدفعه خلال أول ${t.discM} شهراً: الخطط والتقارير وتراخيص العلامة البيضاء.`],
    ['اربح من كل دفعة حقيقية', `تحصل على ${t.comm}٪ من كل دفعة يقومون بها لمدة ${t.commM} شهراً (${t.entM} شهراً إذا اشتروا Enterprise)، دون ضريبة القيمة المضافة. الحسابات المجانية لا تُكسب شيئاً. الزملاء في شركتك يُحتسبون أيضاً؛ وإذا دفعت شركتك، نطلب منها تأكيد علمها بأنك تحصل على عمولة.`],
    ['فترة انتظار', `تبقى كل عمولة «قيد الانتظار» لمدة ${t.hold} يوماً، وهي فترة الاسترداد. إذا استُرد المبلغ للعميل أو اعترض على الدفعة، تُلغى العمولة أو تُخفَّض أو تُجمَّد.`],
    ['تصبح متاحة', `بعد ${t.hold} يوماً تنتقل العمولة إلى «رصيدك المتاح» تلقائياً.`],
    ['اطلب الدفع', `عندما يبلغ رصيدك المتاح ${t.min} دولار، اضغط «طلب سحب» في تبويب السحوبات. في المرة الأولى نطلب بيانات البنك والبيانات التي نحتاجها للضرائب؛ وفي المرة التالية نعبّئها لك.`],
    ['نحوّل لك المبلغ', `ندفع عادةً بتحويل بنكي خلال ${t.pay} يوماً من طلبك، وفي أقصى حد خلال 30 يوماً بعد أن تكتمل لدينا كل البيانات. تصلك رسالة عند الدفع، ويظهر الطلب «مدفوع» في تبويب السحوبات.`],
  ] : [
    ['Share your link or code — personally', `Send it to people you know, one by one, by message, e-mail or in conversation. Please don’t post it publicly: public offers come only from Mid2Bid’s own pages. Everyone who signs up with it gets ${t.disc}% off everything they pay in their first ${t.discM} months: plans, reports and white-label licences.`],
    ['Earn on every real payment', `You earn ${t.comm}% of every payment they make for ${t.commM} months (${t.entM} months if they buy Enterprise), VAT not included. Free accounts earn nothing. Colleagues at your own company count too; if your company pays, we ask it to confirm it knows you receive a commission.`],
    ['A waiting period', `Each commission stays “Pending” for ${t.hold} days, the refund period. If the customer is refunded, or disputes the payment, the commission is cancelled, reduced or frozen.`],
    ['It becomes available', `After ${t.hold} days the commission moves to your “Available Balance” automatically.`],
    ['Ask to be paid', `When your available balance reaches $${t.min}, press “Request Withdrawal” in the Withdrawals tab. The first time, we ask for your bank details and the details we need for tax; next time we fill them in for you.`],
    ['We transfer the money', `We normally pay by bank transfer within ${t.pay} days of your request, and at the latest within 30 days once we have everything we need. You get an e-mail when it is paid, and the request shows “Paid” in the Withdrawals tab.`],
  ];
  return (
    <Card data-testid="referral-how-it-works">
      <CardHeader className="cursor-pointer select-none" onClick={toggle}>
        <CardTitle className="flex items-center justify-between text-lg">
          <span className="flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-emerald-500" />
            {isAr ? 'كيف يعمل برنامج أحِل واربح' : 'How Refer & Earn works'}
          </span>
          {open ? <ChevronUp className="w-5 h-5 text-muted-foreground" /> : <ChevronDown className="w-5 h-5 text-muted-foreground" />}
        </CardTitle>
      </CardHeader>
      {open && (
        <CardContent className="space-y-4">
          <ol className="space-y-3">
            {steps.map(([title, text], i) => (
              <li key={i} className="flex gap-3">
                <span className="shrink-0 w-7 h-7 rounded-full bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 flex items-center justify-center text-sm font-semibold">{i + 1}</span>
                <div>
                  <p className="font-medium text-sm">{title}</p>
                  <p className="text-sm text-muted-foreground">{text}</p>
                </div>
              </li>
            ))}
          </ol>
          <div className="text-xs text-muted-foreground border-t pt-3 space-y-1">
            <p>
              {isAr
                ? 'المبالغ بالدولار الأمريكي. قد يتقاضى بنكك رسوماً لاستلام التحويل. لا يمكنك الربح من مشترياتك الخاصة. لأي سؤال: support@mid2bid.com.'
                : 'Amounts are in US dollars. Your bank may charge a fee to receive the transfer. You cannot earn on your own purchases. Any question: support@mid2bid.com.'}
            </p>
            <p className="flex flex-wrap gap-x-4">
              <span>{isAr ? 'القواعد الكاملة:' : 'Full rules:'}</span>
              <ReferralTermsLink isAr={isAr} />
              <ReferralRulesLink isAr={isAr} />
            </p>
          </div>
        </CardContent>
      )}
    </Card>
  );
}
